import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import TelemetryDeck from '@telemetrydeck/sdk';
import { environment } from '../../../environments/environment';

declare global {
  interface Window {
    Cookiebot: any;
    smartlook: any;
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

@Injectable({
  providedIn: 'root'
})
export class TrackingService {
  private telemetryDeck?: TelemetryDeck;
  private isTelemetryDeckInitialized = false;
  private isSmartlookInitialized = false;
  private isGoogleAnalyticsInitialized = false;
  private previousPath = '';

  constructor(private router: Router) {
    this.setupCookieConsentListeners();
    this.setupNavigationTracking();
  }

  public initializeTracking(): void {
    // check if Cookiebot is available and Statistics-Cookies are allowed
    if (this.isStatisticsConsentGiven()) {
      this.initTelemetryDeck();
      this.initSmartlook();
      this.initGoogleAnalytics();
    }
  }

  public trackEvent(signalType: string, payload?: Record<string, string | number | boolean>): void {
    if (!this.telemetryDeck || !this.isTelemetryDeckInitialized) {
      return;
    }

    try {
      this.telemetryDeck.signal(signalType, payload);
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  private setupCookieConsentListeners(): void {
    if (typeof window !== 'undefined') {
      // Event Listener for Consent Changes
      window.addEventListener('CookiebotOnConsentReady', () => {

        if (this.isStatisticsConsentGiven()) {
          this.initTelemetryDeck();
          this.initGoogleAnalytics();
        }
      });

      // fired when user accepts cookies
      window.addEventListener('CookiebotOnAccept', () => {

        if (this.isStatisticsConsentGiven()) {
          this.initTelemetryDeck();
          this.initGoogleAnalytics();
        }
      });

      // fired when user declines cookies
      window.addEventListener('CookiebotOnDecline', () => {
        if (this.isTelemetryDeckInitialized || this.isSmartlookInitialized || this.isGoogleAnalyticsInitialized) {
          this.telemetryDeck = undefined;
          this.isTelemetryDeckInitialized = false;
          this.isSmartlookInitialized = false;
          this.isGoogleAnalyticsInitialized = false;
          window.location.reload();
        }
      });
    }
  }

  private isStatisticsConsentGiven(): boolean {
    const telemetryDeckAppId = (environment as { telemetryDeckAppId?: string }).telemetryDeckAppId;
    const isCookiebotStatisticsAccepted = typeof window !== 'undefined'
      && window.Cookiebot
      && window.Cookiebot.consent
      && window.Cookiebot.consent.statistics;

    return !!telemetryDeckAppId && isCookiebotStatisticsAccepted && environment.production;
  }

  public initTelemetryDeck(): void {
    if (this.isTelemetryDeckInitialized) {
      return; // Already initialized
    }

    const telemetryDeckAppId = (environment as { telemetryDeckAppId?: string }).telemetryDeckAppId;
    if (!telemetryDeckAppId) {
      Sentry.captureException(new Error('TelemetryDeck App ID not found'));
      return;
    }

    try {
      this.telemetryDeck = new TelemetryDeck({
        appID: telemetryDeckAppId,
        clientUser: this.getTelemetryDeckClientUser(),
      });

      this.telemetryDeck.signal('Page.View');
      this.isTelemetryDeckInitialized = true;
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  private getTelemetryDeckClientUser(): string {
    const existingAnonymousId = localStorage.getItem('telemetryDeckAnonymousId');
    if (existingAnonymousId) {
      return existingAnonymousId;
    } else {
      const anonymousId = `anon-${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem('telemetryDeckAnonymousId', anonymousId);
      return anonymousId;
    }
  }

  private setupNavigationTracking(): void {
    this.router.events.subscribe((event) => {
      if (event.constructor.name === 'NavigationEnd') {
        const navigationEvent = event as { url: string };
        const currentPath = this.formatPath(navigationEvent.url);

        if (currentPath !== this.previousPath) {
          this.trackNavigation(this.previousPath, currentPath);
          this.previousPath = currentPath;
        }
      }
    });
  }

  private formatPath(url: string): string {
    const path = url.split('?')[0].split('#')[0];
    return path || '/';
  }

  private trackNavigation(source: string, destination: string): void {
    if (!this.telemetryDeck || !this.isTelemetryDeckInitialized) {
      return;
    }

    try {
      this.telemetryDeck.signal('TelemetryDeck.Navigation.pathChanged', {
        'TelemetryDeck.Navigation.schemaVersion': '1',
        'TelemetryDeck.Navigation.identifier': `${source} -> ${destination}`,
        'TelemetryDeck.Navigation.sourcePath': source,
        'TelemetryDeck.Navigation.destinationPath': destination
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  public initSmartlook(): void {
    if (this.isSmartlookInitialized) {
      return; // Already initialized
    }

    try {

      // dynamically insert the Smartlook-Script
      window.smartlook ||
        (function (d) {
          var o = (window.smartlook = function () {
            (o as any).api.push(arguments);
          }),
            h = d.getElementsByTagName('head')[0];
          var c = d.createElement('script');
          (o as any).api = new Array();
          c.async = true;
          c.type = 'text/javascript';
          c.charset = 'utf-8';
          c.src = 'https://web-sdk.smartlook.com/recorder.js';
          h.appendChild(c);
        })(document);

      // initialize Smartlook with configuration
      window.smartlook('init', 'd9182401a7ac419bb2df0bbf0be35b9ccb7181d2', {
        region: 'eu',
        advancedNetwork: {
          allowedUrls: [new RegExp('^https://backend.opus-m.de/')],
        },
        interceptors: {
          network: (data: any, context: any) => {
            const tokenIndex = data.url.indexOf('token=');
            if (tokenIndex > -1) {
              data.url = data.url.slice(0, tokenIndex) + 'token=[OBSCURED]';
            }
          },
        },
      });

      // configure recording settings
      window.smartlook('record', { forms: true, numbers: true, emails: true, ips: true });

      this.isSmartlookInitialized = true;
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  public initGoogleAnalytics(): void {
    if (this.isGoogleAnalyticsInitialized) {
      return; // Already initialized
    }

    try {
      // Initialize dataLayer for Google Analytics
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };

      // Set the initial timestamp
      window.gtag('js', new Date());

      // Configure your Google Analytics property
      window.gtag('config', 'G-WHXEDEDX9Q');

      // dynamically insert the Google Analytics script
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-WHXEDEDX9Q';

      const firstScript = document.getElementsByTagName('script')[0];
      firstScript.parentNode?.insertBefore(script, firstScript);

      this.isGoogleAnalyticsInitialized = true;
    } catch (error) {
      Sentry.captureException(error);
    }
  }
}